import type { LoanTerms } from 'constants/loanTerms'

/** Start from the USD to borrow, or from the BTC to pledge. */
export type LoanInputMode = 'usd' | 'btc'

export interface LoanInput {
  mode: LoanInputMode
  /** USD to borrow, or BTC to pledge, per `mode`. */
  amount: number
  ltv: number
  months: number
  /** USD per BTC. */
  btcPrice: number
}

export interface LoanQuote {
  loan: number
  btc: number
  /** USD value of the pledged BTC. */
  collateral: number
  interest: number
  total: number
  /** The BTC prices at which the loan reaches each level. */
  firstMarginCallPrice: number
  secondMarginCallPrice: number
  liquidationPrice: number
  /** Where the loan sits against the allowed range. */
  range: 'below' | 'within' | 'above'
}

export const calculateLoan = ({ mode, amount, ltv, months, btcPrice }: LoanInput, terms: LoanTerms): LoanQuote => {
  const loan = mode === 'usd' ? amount : amount * btcPrice * ltv
  const btc = mode === 'usd' ? amount / (ltv * btcPrice) : amount
  const interest = loan * terms.rate * (months / 12)
  // LTV = loan / (btc × price); it reaches `level` when the price falls to price × ltv / level.
  const priceAt = (level: number) => (btcPrice * ltv) / level
  return {
    loan,
    btc,
    collateral: btc * btcPrice,
    interest,
    total: loan + interest,
    firstMarginCallPrice: priceAt(terms.firstMarginCall),
    secondMarginCallPrice: priceAt(terms.secondMarginCall),
    liquidationPrice: priceAt(terms.liquidation),
    range: loan < terms.minLoan ? 'below' : loan > terms.maxLoan ? 'above' : 'within',
  }
}

/** The amount in the other mode's unit, so switching keeps the same loan. */
export const convertLoanAmount = (amount: number, to: LoanInputMode, ltv: number, btcPrice: number): number =>
  to === 'btc' ? amount / (ltv * btcPrice) : amount * btcPrice * ltv

/** The repayment date: `months` after `start`, in UTC (the 31st rolls back to the month's last day). */
export const loanMaturity = (start: number, months: number): number => {
  const date = new Date(start)
  const day = date.getUTCDate()
  date.setUTCDate(1)
  date.setUTCMonth(date.getUTCMonth() + months)
  const lastDay = new Date(Date.UTC(date.getUTCFullYear(), date.getUTCMonth() + 1, 0)).getUTCDate()
  date.setUTCDate(Math.min(day, lastDay))
  return date.getTime()
}
